import { useEffect, useState } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import {
  LayoutDashboard, MapPin, Users, Megaphone, Tag, Building2,
  LogOut, Menu, X, Layers, Briefcase,
} from 'lucide-react';

const NAV = [
  { to: '/admin/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
  { to: '/admin/cidades', icon: MapPin, label: 'Cidades' },
  { to: '/admin/categorias', icon: Layers, label: 'Categorias' },
  { to: '/admin/tipos-empresa', icon: Briefcase, label: 'Tipos de Empresa' },
  { to: '/admin/profissionais', icon: Users, label: 'Profissionais' },
  { to: '/admin/anuncios', icon: Megaphone, label: 'Anúncios' },
  { to: '/admin/promocoes', icon: Tag, label: 'Promoções' },
  { to: '/admin/anunciantes', icon: Building2, label: 'Anunciantes' },
];

export function AdminLayout({ title, children }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const token = sessionStorage.getItem('adminToken');

  useEffect(() => {
    if (!token) navigate('/admin/login');
  }, [token, navigate]);

  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  const logout = () => {
    sessionStorage.removeItem('adminToken');
    navigate('/admin/login');
  };

  return (
    <div className="min-h-screen bg-gray-50 flex">
      <aside className="hidden lg:flex flex-col w-60 bg-white border-r border-gray-100 h-screen sticky top-0">
        <SidebarContent onLogout={logout} />
      </aside>

      {mobileOpen && (
        <div className="lg:hidden fixed inset-0 z-40 bg-black/50" onClick={() => setMobileOpen(false)}>
          <aside className="w-64 h-full bg-white" onClick={(e) => e.stopPropagation()}>
            <SidebarContent onLogout={logout} onClose={() => setMobileOpen(false)} />
          </aside>
        </div>
      )}

      <div className="flex-1 min-w-0 flex flex-col">
        <header className="bg-white border-b border-gray-100 sticky top-0 z-20">
          <div className="px-4 lg:px-8 py-4 flex items-center gap-3">
            <button
              onClick={() => setMobileOpen(true)}
              className="lg:hidden p-2 hover:bg-gray-100 rounded-lg"
              data-testid="admin-mobile-menu-btn"
            >
              <Menu size={18} />
            </button>
            <h1 className="font-bold text-gray-900 text-base lg:text-lg" style={{ fontFamily: "'Work Sans', sans-serif" }}>
              {title}
            </h1>
          </div>
        </header>

        <main className="flex-1 p-4 lg:p-8">{children}</main>
      </div>
    </div>
  );
}

function SidebarContent({ onLogout, onClose }) {
  return (
    <>
      <div className="px-5 py-5 border-b border-gray-100 flex items-center justify-between">
        <div className="flex items-center gap-2.5">
          <div className="w-9 h-9 bg-[#1D9E75] rounded-xl flex items-center justify-center">
            <span className="text-white font-bold">G</span>
          </div>
          <div>
            <p className="font-bold text-gray-900 text-sm leading-tight" style={{ fontFamily: "'Work Sans', sans-serif" }}>
              GuiaCit
            </p>
            <p className="text-[11px] text-gray-500 leading-tight">Painel Admin</p>
          </div>
        </div>
        {onClose && (
          <button onClick={onClose} className="p-1.5 hover:bg-gray-100 rounded-lg">
            <X size={16} />
          </button>
        )}
      </div>

      <nav className="flex-1 px-3 py-4 space-y-0.5 overflow-y-auto">
        {NAV.map(({ to, icon: Icon, label }) => (
          <NavLink
            key={to}
            to={to}
            data-testid={`nav-${label.toLowerCase()}`}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2 rounded-xl text-sm transition-colors ${
                isActive
                  ? 'bg-[#E1F5EE] text-[#1D9E75] font-semibold'
                  : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
              }`
            }
          >
            <Icon size={16} />
            {label}
          </NavLink>
        ))}
      </nav>

      <div className="p-3 border-t border-gray-100">
        <button
          onClick={onLogout}
          data-testid="admin-logout-btn"
          className="w-full flex items-center gap-2.5 px-3 py-2 rounded-xl text-sm text-gray-600 hover:bg-red-50 hover:text-red-600 transition-colors"
        >
          <LogOut size={16} />
          Sair
        </button>
      </div>
    </>
  );
}
